import type { InputHTMLAttributes } from "react";
import type { FilterOption } from "./FilterSelect";

interface FormFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  multiline?: boolean;
  required?: boolean;
  prefix?: string;
  suffix?: string;
  error?: string | null;
}

export function FormField({
  label,
  value,
  onChangeText,
  multiline,
  required,
  prefix,
  suffix,
  error,
  placeholder,
  type,
  inputMode,
  ...rest
}: FormFieldProps) {
  const borderClass = error
    ? "border-destructive"
    : "border-border focus-within:border-primary";

  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <label className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <div
        className={`flex items-center gap-1.5 rounded-md border bg-card px-3 ${borderClass}`}
      >
        {prefix && (
          <span className="text-sm font-medium text-muted-foreground">
            {prefix}
          </span>
        )}
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChangeText(e.target.value)}
            placeholder={placeholder}
            className="min-h-[80px] w-full resize-y bg-transparent py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChangeText(e.target.value)}
            placeholder={placeholder}
            type={type ?? "text"}
            inputMode={inputMode}
            className="min-h-[44px] w-full bg-transparent py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
            {...rest}
          />
        )}
        {suffix && (
          <span className="pl-1.5 text-sm font-medium text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: string[];
  optionLabels?: string[];
  onSelect: (v: string) => void;
  required?: boolean;
  error?: string | null;
}

export function SelectField({
  label,
  value,
  options,
  optionLabels,
  onSelect,
  required,
  error,
}: SelectFieldProps) {
  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <label className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt, i) => {
          const selected = value === opt;
          const chipClass = selected
            ? "border-secondary bg-secondary/20 text-secondary-foreground font-semibold"
            : error
              ? "border-destructive bg-destructive/10 text-muted-foreground"
              : "border-border bg-card text-muted-foreground hover:border-secondary/60";
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onSelect(opt)}
              className={`rounded-md border px-2.5 py-1.5 text-xs transition-colors ${chipClass}`}
            >
              {optionLabels?.[i] ?? opt}
            </button>
          );
        })}
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

type NumericFieldProps = Pick<
  FormFieldProps,
  "label" | "value" | "onChangeText" | "required" | "error" | "placeholder"
>;

/**
 * Sanitize a decimal-number string at every keystroke. Strips anything that
 * isn't a digit or a single decimal point — letters (including the `e`/`E`
 * that `type="number"` would otherwise allow), arithmetic signs, or pasted
 * junk all get dropped before state is updated.
 */
function sanitizeDecimal(raw: string): string {
  let cleaned = raw.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }
  return cleaned;
}

function sanitizeInteger(raw: string): string {
  return raw.replace(/\D/g, "");
}

/** Currency input: $ prefix, numeric keypad on mobile, formats to 2 decimals
 *  on blur (e.g. "12.5" → "12.50"). State stays string-backed; parse on save.
 *  Uses type="text" + sanitizing onChange so desktop browsers can't slip
 *  letters / `e` notation / signs through `type="number"`. */
export function MoneyField({
  value,
  onChangeText,
  placeholder = "0.00",
  ...rest
}: NumericFieldProps) {
  return (
    <FormField
      {...rest}
      value={value}
      onChangeText={(v) => onChangeText(sanitizeDecimal(v))}
      placeholder={placeholder}
      prefix="$"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      onBlur={() => {
        const n = parseFloat(value);
        if (value.trim() !== "" && !Number.isNaN(n)) onChangeText(n.toFixed(2));
      }}
    />
  );
}

/** Integer count input: numeric keypad on mobile, digits only.
 *  Same type="text" + sanitize pattern as MoneyField for desktop correctness. */
export function NumberField({
  value,
  onChangeText,
  placeholder,
  ...rest
}: NumericFieldProps) {
  return (
    <FormField
      {...rest}
      value={value}
      onChangeText={(v) => onChangeText(sanitizeInteger(v))}
      placeholder={placeholder}
      type="text"
      inputMode="numeric"
      autoComplete="off"
    />
  );
}

/**
 * Form select rendered as a native dropdown (vs SelectField's chip row). Use
 * for required single-choice fields. Options are plain strings (value === label)
 * or {value,label} pairs; unlike FilterSelect there is no implicit "All" — pass
 * exactly the options you want, including any "none" entry.
 */
export function DropdownField({
  label,
  value,
  options,
  onChange,
  required,
  error,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (v: string) => void;
  required?: boolean;
  error?: string | null;
}) {
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const borderClass = error
    ? "border-destructive"
    : "border-border focus-within:border-primary";
  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <label className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <div
        className={`flex items-center rounded-md border bg-card px-3 ${borderClass}`}
      >
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[44px] w-full bg-transparent py-3 text-[15px] text-foreground focus:outline-none"
        >
          {normalized.map((o) => (
            <option
              key={o.value}
              value={o.value}
              className="bg-card text-foreground"
            >
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
